<script setup lang="ts" generic="T extends Record<string, any>">
import { Icon } from "@iconify/vue";
import { computed, ref, onBeforeUnmount, onMounted } from "vue";
import { useDataTableWidget } from "../composables/useDataTableWidget";

const props = defineProps<{
  table: ReturnType<typeof useDataTableWidget<T>>;
  /**
   * Tinggi maksimal body tabel (mis. "200px" atau 200). Opsional.
   * Kalau tidak diisi, akan dihitung otomatis dari tinggi viewport.
   */
  maxHeight?: string | number;
}>();

const rootEl = ref<HTMLElement | null>(null);
const windowHeight = ref(window.innerHeight);

const resizeHandler = () => {
  windowHeight.value = window.innerHeight;
};

onMounted(() => {
  window.addEventListener("resize", resizeHandler);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", resizeHandler);
});

const resolvedMaxHeight = computed<string | number | undefined>(() => {
  return props.maxHeight ?? props.table.maxHeight ?? Math.max(120, windowHeight.value - 205);
});

// Ekspos elemen root agar bisa diakses parent (mis. untuk pengukuran/scroll)
defineExpose({ rootEl });

/** Lebar scroll horizontal total bila ada kolom fixed.
 *  Naive-ui butuh scroll-x eksplisit saat memakai kolom fixed; tanpa itu
 *  semua kolom dipaksa mengecil (table-layout: fixed) sampai teks patah
 *  satu kata per baris. Angka per kolom: width ?? minWidth, fallback 150. */
const scrollX = computed<number | undefined>(() => {
  const cols = (props.table.computedVisibleColumns.value ?? []) as Array<{
    fixed?: "left" | "right" | boolean;
    width?: number;
    minWidth?: number;
  }>;
  if (!cols.some((c) => c.fixed)) return undefined;
  return cols.reduce((total, c) => total + (c.width ?? c.minWidth ?? 150), 0);
});
</script>

<template>
  <div ref="rootEl" class="overflow-hidden h-full">
    <!-- Bar bulk action: muncul kalau ada row yang dicentang -->
    <div v-if="table.isSelectionEnabled && table.selectedCount.value > 0" class="flex items-center gap-2 mb-3 px-3 py-2 rounded-md bg-blue-50 border border-blue-100">
      <span class="text-sm font-medium">{{ table.selectedCount.value }} dipilih</span>
      <n-button text type="primary" size="small" @click.stop="table.clearSelection">Batal pilih</n-button>

      <div class="ml-auto flex gap-2">
        <template v-for="action in table.bulkActions" :key="action.key">
          <n-button
            v-if="table.isBulkActionVisible(action)"
            :type="action.type ?? 'default'"
            size="small"
            :disabled="table.isBulkActionDisabled(action)"
            :loading="table.bulkLoadingKey.value === action.key"
            @click.stop="table.runBulkAction(action)"
          >
            <template v-if="action.icon" #icon>
              <Icon :icon="action.icon" />
            </template>
            {{ action.label }}
          </n-button>
        </template>
      </div>
    </div>

    <n-data-table
      :columns="table.computedVisibleColumns.value"
      :data="table.data.value"
      :loading="table.isDataLoading.value || table.isPageLoading.value"
      :row-key="table.rowKey"
      :checked-row-keys="table.checkedRowKeys.value"
      @update:checked-row-keys="table.handleCheckedRowKeysChange"
      :row-props="table.rowProps"
      :max-height="resolvedMaxHeight"
      :scroll-x="scrollX"
      :sorter="{
        columnKey: table.sortBy.value || '',
        order: table.sortOrder.value === 'asc' ? 'ascend' : table.sortOrder.value === 'desc' ? 'descend' : false,
      }"
      @update:sorter="table.handleSorterChange"
      remote
      :size="table.size.value"
    >
    </n-data-table>
  </div>
</template>

<style scoped>
/* Sel tabel rata atas secara default (naive-ui mewarisi vertical-align:
   middle dari default td) supaya baris multi-baris rapi dibaca dari atas. */
:deep(.n-data-table-td) {
  vertical-align: top;
}
</style>
